import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  signal,
  input
} from "@angular/core";
import {
  IonicModule,
  ModalController,
  Platform,
  PopoverController,
} from "@ionic/angular";
import { EducationCategoryDto } from "src/app/settings/interfaces/education_category_dto";
import { StudentDto } from "src/app/students/interfaces/student_dto";
import { ModeService } from "src/app/utils/mode.service";
import { LessonRatingDto } from "../../interfaces/lesson_rating";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import {
  addLessonRatings,
  loadEducationBodies,
  loadEducationTitles,
  removeLessonRatings,
} from "src/app/settings/ngrx/settings.action";
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
  takeUntil,
} from "rxjs";
import {
  selectLessonRatingsForStudent,
  selectLessonRatingsForLesson,
  selectRemovingLessonRatings,
  selectAddingLessonRatings,
} from "src/app/settings/ngrx/settings.selectors";
import { AsyncPipe, DatePipe, NgClass, NgStyle } from "@angular/common";
import { Browser } from "@capacitor/browser";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";
import * as appointmentSelector from "src/app/calendar/ngrx/calendar.selectors";
import { CommentDialogComponent } from "src/app/tracking/comment-dialog/comment-dialog.component";
import { CommentPopoverComponent } from "../../../shared/components/comment-popover/comment-popover.component";

@Component({
    selector: "app-education-card",
    templateUrl: "./education-card.component.html",
    styleUrls: ["./education-card.component.scss"],
    imports: [
        IonicModule,
        ReactiveFormsModule,
        FormsModule,
        AsyncPipe,
        NgClass,
        NgStyle,
        DatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EducationCardComponent {
  readonly educationCategory = input<EducationCategoryDto>(null);
  readonly student = input<StudentDto>(null);
  readonly appointment = input<AppointmentDto>(null);
  readonly readMode = input<boolean>(false);

  displayedEducationCategory: EducationCategoryDto;
  currentLessonRatings$: Observable<LessonRatingDto[]>;
  studentLessonRatings$: Observable<LessonRatingDto[]>;
  pastLessons$: Observable<AppointmentDto[]>;
  plannedLessonRatings: LessonRatingDto[];
  oldLessonRatings: LessonRatingDto[];
  lessonRatingForm: FormGroup;
  pristineLessonRatingForm: FormGroup;
  isPlanning: boolean = false;
  isRating: boolean = false;
  pastLessons: AppointmentDto[] = [];
  displayedLessons: AppointmentDto[] = [];
  section: "planned" | "all" = "all";
  numberOfRatingsDisplayed = signal(4);
  listComments = signal(false);
  hasComments = signal(false);
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  addingLessonRatings$ = this.store.select(selectAddingLessonRatings);
  removingLessonRatings$ = this.store.select(selectRemovingLessonRatings);

  mobile = true;
  constructor(
    public modeService: ModeService,
    private modalController: ModalController,
    private fb: FormBuilder,
    private store: Store<any>,
    private cdr: ChangeDetectorRef,
    private platform: Platform,
    private popoverController: PopoverController
  ) {
    this.lessonRatingForm = this.fb.group({});

    combineLatest([this.addingLessonRatings$, this.removingLessonRatings$]).pipe(
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([adding, removing]) => {
      if (!adding && !removing) {
        this.isPlanning = false;
        this.isRating = false;
      }
    });
  }

  ionViewDidEnter() {
    window?.addEventListener("resize", this.handleDisplayedRatings.bind(this));
    this.store.dispatch(loadEducationTitles());
    this.store.dispatch(loadEducationBodies());
    this.studentLessonRatings$ = this.store.select(
      selectLessonRatingsForStudent(this.student().student_id)
    );

    this.studentLessonRatings$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe((lessonRatings) => {
        this.oldLessonRatings = lessonRatings;
        this.handleDisplayedEducations();
      });

    this.pastLessons$ = this.store.select(
      appointmentSelector.selectLessonsByStudentId(this.student().student_id)
    );
    this.pastLessons$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => {
          return prev.length === curr.length;
        })
      )
      .subscribe((lessons) => {
        this.pastLessons = lessons.sort(
          (a, b) => a.StartTime.getTime() - b.StartTime.getTime()
        );
      });

    if (this.appointment()) {
      this.currentLessonRatings$ = this.store.select(
        selectLessonRatingsForLesson(this.appointment().Id)
      );

      // Initialize form controls based on lessonRatings$
      this.currentLessonRatings$
        .pipe(
          takeUntil(this.ngUnsubscribe),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe((lessonRatings) => {
          if (lessonRatings) {
            this.plannedLessonRatings = lessonRatings;
          }

          this.buildLessonRatingForm();
          if (this.plannedLessonRatings?.length > 0) {
            this.section = "planned";
          }
          this.handleDisplayedEducations();
        });
    } else {
      this.buildLessonRatingForm();
    }

    this.handleDisplayedRatings();

    this.studentLessonRatings$
      .pipe(
        map((ratings) => this.hasAnyComments(ratings)),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((hasComments) => {
        this.hasComments.set(hasComments);
      });
  }

  private hasAnyComments(ratings: LessonRatingDto[]): boolean {
    return ratings?.some((rating) => rating?.notes) ?? false;
  }

  handleDisplayedRatings() {
    if (!(this.readMode() && this.appointment() !== null)) {
      this.numberOfRatingsDisplayed.set(
        Math.floor(this.platform.width() / 42) - 6 > this.pastLessons.length
          ? this.pastLessons.length
          : Math.floor(this.platform.width() / 42) - 6);

      if (this.appointment()) {
        const appointmentIndex = this.pastLessons?.indexOf(this.appointment());

        if (this.pastLessons.length <= this.numberOfRatingsDisplayed()) {
          // If total lessons are less than or equal to numberOfRatingsDisplayed, show all lessons
          this.displayedLessons = this.pastLessons.slice();
        } else {
          // Calculate the start index so that the current appointment is the last lesson in the displayed lessons if there are enough past lessons
          const startIndex = Math.max(
            0,
            appointmentIndex - this.numberOfRatingsDisplayed() + 1
          );
          if ((appointmentIndex + 1) < this.numberOfRatingsDisplayed()) {
            this.numberOfRatingsDisplayed.set(appointmentIndex + 1);
          }
          this.displayedLessons = this.pastLessons.slice(
            startIndex,
            appointmentIndex + 1
          );
          // If the calculated range is less than numberOfRatingsDisplayed, adjust to show the correct number of lessons
          if (this.displayedLessons.length < this.numberOfRatingsDisplayed()) {
            const additionalLessons =
              this.numberOfRatingsDisplayed() - this.displayedLessons.length;
            const sliceStart = Math.max(0, startIndex - additionalLessons);
            this.displayedLessons = this.pastLessons.slice(
              sliceStart,
              appointmentIndex + 1
            );
          }
        }
      } else {
        this.displayedLessons = this.pastLessons.slice(
          0,
          this.numberOfRatingsDisplayed()
        );
      }
    } else {
      this.numberOfRatingsDisplayed.set(1);
      this.displayedLessons = [this.appointment()];
    }

    this.cdr.markForCheck();
  }

  buildLessonRatingForm() {
    // reset form
    this.lessonRatingForm = this.fb.group({});

    this.plannedLessonRatings?.forEach((rating) => {
      const controlName = rating.education_body_id.toString();
      this.lessonRatingForm.addControl(
        controlName,
        this.fb.group({
          lesson_rating_id: this.fb.control(rating.lesson_rating_id),
          selected: this.fb.control(true),
          rating: this.fb.control(rating.rating),
          notes: this.fb.control(rating.notes),
        })
      );
    });

    this.handleDisplayedEducations();

    // Iterate over education_category, education_title, and education_body
    this.educationCategory().education_title.forEach((educationTitle) => {
      educationTitle.education_body.forEach((educationBody) => {
        const controlName = educationBody.education_body_id.toString();

        // Add control only if it doesn't exist (not added from lessonRatings$)
        if (!this.lessonRatingForm.get(controlName)) {
          this.lessonRatingForm.addControl(
            controlName,
            this.fb.group({
              lesson_rating_id: this.fb.control(null),
              selected: this.fb.control(false),
              rating: this.fb.control(null),
              notes: this.fb.control(null),
            })
          );
        }
      });
    });

    this.pristineLessonRatingForm = this.lessonRatingForm.value;
  }

  onRatingChange(event, educationBody) {
    const controlName = educationBody.education_body_id.toString();
    this.lessonRatingForm.get(controlName + ".selected").setValue(true);
  }

  ngOnDestroy() {
    window.removeEventListener(
      "resize",
      this.handleDisplayedRatings.bind(this)
    );
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  cancel() {
    this.modalController.getTop().then((modal) => {
      modal.dismiss();
    });
  }

  toggleIsPlanning() {
    this.isPlanning = !this.isPlanning;
  }

  toggleIsRating() {
    this.isRating = !this.isRating;
    this.cdr.detectChanges();
  }

  openCommentDialog(educationBodyId: number) {
    var formControl = this.lessonRatingForm.get(
      educationBodyId.toString() + ".notes"
    );
    this.lessonRatingForm.get(educationBodyId.toString() + ".selected").setValue(true);

    this.modalController
      .create({
        component: CommentDialogComponent,
        breakpoints: [0, 0.8, 1],
        initialBreakpoint: 0.8,
        componentProps: {
          comment: signal(formControl.value),
        },
      })
      .then((modal) => {
        modal.onDidDismiss().then((data) => {
          if (data.role === "delete") {
            formControl.setValue(null);
          }
          if (data.data) {
            formControl.setValue(data.data);
          }

          this.cdr.markForCheck();
        });
        return modal.present();
      });
  }

  onSubmit() {
    const selectedEducationBodyIds = Object.keys(this.lessonRatingForm.controls)
      .filter((key) => this.lessonRatingForm.get(key + ".selected").value)
      .map((key) => parseInt(key, 10));

    // create lesson ratings based on selectedEducationBodyIds and dispatch addLessonRatings
    const newLessonRatings = selectedEducationBodyIds.map((educationBodyId) => {
      const controlName = educationBodyId.toString();
      return {
        user_id: null,
        lesson_rating_id: this.lessonRatingForm.get(
          controlName + ".lesson_rating_id"
        ).value,
        education_body_id: educationBodyId,
        created_at: new Date().toISOString(),
        appointment_id: this.appointment().Id,
        student_id: this.student().student_id,
        rating: this.lessonRatingForm.get(controlName + ".rating").value,
        notes: this.lessonRatingForm.get(controlName + ".notes").value,
      };
    });

    if (newLessonRatings.length === 0) {
      this.section = "all";
      this.handleDisplayedEducations();
    }

    if (newLessonRatings?.length > 0) {
      this.store.dispatch(
        addLessonRatings({ lessonRatings: newLessonRatings })
      );
    }

    // remove the lessonRatings which are not true in the form anymore (but are in the lessonRatings$)
    const lessonRatingsToRemove = this.plannedLessonRatings?.filter(
      (rating) =>
        !this.lessonRatingForm.get(
          rating.education_body_id.toString() + ".selected"
        )?.value
    );

    if (lessonRatingsToRemove?.length > 0) {
      this.store.dispatch(
        removeLessonRatings({ lessonRatings: lessonRatingsToRemove })
      );
    }

    this.pristineLessonRatingForm = this.lessonRatingForm.value;
  }

  onCancel() {
    this.isPlanning = false;
    this.isRating = false;

    this.lessonRatingForm.reset(this.pristineLessonRatingForm);
  }

  segmentChanged(event) {
    this.section = event;
    this.handleDisplayedEducations();
  }

  getRating(educationBody, lesson = null) {
    // Check if there's a rating in lessonRatingForm, otherwise get the old rating
    const lessonRating = this.lessonRatingForm?.get(
      educationBody?.education_body_id?.toString() + ".rating"
    )?.value;

    if ((this.readMode() && this.appointment() !== null) || this.isRating) {
      if (lessonRating !== null && lessonRating !== undefined)
        return lessonRating;
      else return null;
    }

    // Find the old ratings for the education body from oldLessonRatings
    const oldRating = this.oldLessonRatings?.find(
      (rating) =>
        rating?.education_body_id === educationBody?.education_body_id &&
        rating?.appointment_id === lesson?.Id
    );

    return oldRating?.rating;
  }

  getComment(educationBody, lesson = null) {
    // Check if there's a comment in lessonRatingForm, otherwise get the old comment
    const lessonComment = this.lessonRatingForm?.get(
      educationBody?.education_body_id?.toString() + ".notes"
    )?.value;

    if ((this.readMode() && this.appointment() !== null) || this.isRating) {
      if (lessonComment !== null && lessonComment !== undefined) {
        return lessonComment;
      } else return null;
    }

    // Find the old comment for the education body from oldLessonRatings
    const oldComment = this.oldLessonRatings?.find(
      (rating) =>
        rating?.education_body_id === educationBody?.education_body_id &&
        rating?.appointment_id === lesson?.Id
    );

    return oldComment?.notes;
  }

  getAllEducationBodyComments(educationBody) {
    const comments = this.oldLessonRatings
      ?.filter(
        (rating) =>
          rating?.education_body_id === educationBody?.education_body_id
      )
      ?.map((comment) => comment.notes);

    // return the comments but filter out null values and undefined values
    return comments.filter(
      (comment) => comment !== null && comment !== undefined
    );
  }

  async showComment(educationBody, lesson = null) {
    var comment = this.getComment(educationBody, lesson);

    if (!comment) return;

    const popover = await this.popoverController.create({
      component: CommentPopoverComponent,
      componentProps: {
        comment: signal(comment),
      },
    });

    await popover.present();
  }

  navLeft() {
    const firstDisplayedIndex = this.pastLessons?.indexOf(
      this.displayedLessons[0]
    );
    if (firstDisplayedIndex > 0) {
      this.displayedLessons = [
        this.pastLessons[firstDisplayedIndex - 1],
        ...this.displayedLessons.slice(0, this.displayedLessons.length - 1),
      ];
      this.cdr.detectChanges();
    }
  }

  navRight() {
    const lastDisplayedIndex = this.pastLessons?.indexOf(
      this.displayedLessons[this.displayedLessons.length - 1]
    );
    if (lastDisplayedIndex < this.pastLessons.length - 1) {
      this.displayedLessons = [
        ...this.displayedLessons.slice(1),
        this.pastLessons[lastDisplayedIndex + 1],
      ];
      this.cdr.detectChanges();
    }
  }

  handleDisplayedEducations() {
    if (this.section === "planned") {
      this.displayedEducationCategory = {
        ...this.educationCategory(),
        education_title: this.educationCategory()?.education_title
          ?.map((educationTitle) => {
            return {
              ...educationTitle,
              education_body: educationTitle.education_body.filter(
                (educationBody) =>
                  this.lessonRatingForm?.get(
                    educationBody.education_body_id.toString() + ".selected"
                  )?.value
              ),
            };
          })
          .filter((educationTitle) => educationTitle.education_body.length > 0),
      };
    } else {
      this.displayedEducationCategory = { ...this.educationCategory() };
    }

    const sortedEducationTitles = [
      ...this.displayedEducationCategory.education_title,
    ].sort((a, b) => a.row_order - b.row_order);

    const sortedEducationBodies = sortedEducationTitles.map(
      (educationTitle) => {
        return {
          ...educationTitle,
          education_body: [...educationTitle.education_body].sort(
            (a, b) => a.row_order - b.row_order
          ),
        };
      }
    );

    this.displayedEducationCategory = {
      ...this.displayedEducationCategory,
      education_title: sortedEducationBodies,
    };

    this.handleDisplayedRatings();

    this.cdr.detectChanges();
  }

  async openHyperLink(link: string) {
    if (link) {
      await Browser.open({ url: link });
    }
  }
}
